import React from 'react';
import Header from './shared/Header'; // Assuming shared components are in a 'shared' folder
import Footer from './Footer'; 
import SEO from './SEO';
import {
  ScaleIcon, ChatBubbleLeftRightIcon, CpuChipIcon,
  BanknotesIcon, CheckBadgeIcon, ArrowTrendingUpIcon, ShieldExclamationIcon,
  WrenchScrewdriverIcon, // Added for Portfolio Construction
  DocumentChartBarIcon // Added for Overview
} from '@heroicons/react/20/solid'; // Using solid icons for consistency

// Methodology Factors Data (copied for reference, descriptions expanded below)
const altDataFactors = [
  {
    name: 'Earnings Expectations',
    icon: ScaleIcon,
    description: 'Goes beyond consensus by analyzing sell-side analyst quality, historical accuracy, peer estimate trends, and corporate guidance nuances. Generates proprietary forward-looking EPS & Revenue forecasts designed to be more predictive than street estimates alone.',
    details: [
        "Analyzes individual analyst track records and revision patterns.",
        "Incorporates peer-group estimate momentum.",
        "Models the impact of company guidance language and tone.",
        "Aims to capture estimate drift before it's fully reflected in consensus."
    ]
  },
  {
    name: 'Earnings Calls Sentiment',
    icon: ChatBubbleLeftRightIcon,
    description: 'Utilizes advanced Natural Language Processing (NLP) models trained on financial language to analyze earnings call transcripts. Captures subtle shifts in management tone, sentiment complexity, and forward-looking statements often missed by simple keyword analysis.',
    details: [
        "Detects sentiment beyond simple positive/negative scores.",
        "Identifies changes in tone regarding specific business segments or risks.",
        "Quantifies forward guidance strength and conviction level.",
        "Provides an orthogonal signal to traditional earnings surprise metrics."
    ]
  },
  {
    name: 'Innovation Signal',
    icon: CpuChipIcon,
    description: 'Measures a company\'s innovation capacity and future growth potential by analyzing patent data (quality, citations, scope) and skilled labor acquisition (e.g., foreign H1B visa filings for technical roles). Identifies firms investing in potentially disruptive R&D.',
    details: [
        "Focuses on patent quality metrics, not just quantity.",
        "Tracks patent citations and technological breadth.",
        "Uses H1B visa data as a proxy for attracting specialized talent.",
        "Aims to identify future market leaders based on intangible R&D investment."
    ]
  },
];

const quantFactors = [
  {
    name: 'Enhanced Value',
    icon: BanknotesIcon,
    description: 'Moves beyond static, historical valuation ratios. Incorporates forward-looking earnings expectations and adjusts for sector-specific biases and accounting differences, providing a more dynamic and comparable measure of intrinsic value.',
    details: [
        "Integrates proprietary earnings forecasts into valuation models.",
        "Normalizes ratios based on industry characteristics.",
        "Adjusts for differences in accounting standards (e.g., R&D capitalization).",
        "Seeks stocks mispriced relative to their forward fundamental outlook."
    ]
  },
  {
    name: 'Enhanced Quality',
    icon: CheckBadgeIcon,
    description: 'Focuses on the sustainability and reliability of earnings, not just profitability levels. Differentially weights accrual components based on their persistence and analyzes cash flow generation consistency and balance sheet strength.',
    details: [
        "Penalizes earnings driven by unsustainable accruals.",
        "Prioritizes strong, stable cash flow generation over accounting profits.",
        "Assesses balance sheet risk and leverage appropriateness.",
        "Identifies companies with durable competitive advantages reflected in financials."
    ]
  },
  {
    name: 'Enhanced Momentum',
    icon: ArrowTrendingUpIcon,
    description: 'Refines traditional price momentum by utilizing proprietary peer-group benchmarking based on economic linkages, not just GICS codes. Captures cross-stock effects and industry flows, mitigating noise from broad market movements.',
    details: [
        "Constructs dynamic peer groups based on supply chain, customer, and competitor data.",
        "Measures stock performance relative to its true economic peers.",
        "Identifies momentum driven by company-specific news vs. sector trends.",
        "Aims for a more robust and less crowded momentum signal."
    ]
  },
   {
    name: 'Enhanced Volatility',
    icon: ShieldExclamationIcon,
    description: 'Adjusts traditional low-volatility metrics to isolate the component predictive of positive future returns. Controls for unintended exposures to other factors (like value or quality) to present a purer measure of beneficial, risk-reducing volatility.',
     details: [
        "Neutralizes common factor tilts often found in simple low-vol strategies.",
        "Focuses on idiosyncratic volatility reduction.",
        "Analyzes volatility patterns over different time horizons.",
        "Seeks stocks offering diversification benefits beyond simple low price swings."
    ]
  },
];


export default function MethodologyPage() {
  return (
    <>
      <SEO 
        title="Methodology"
        description="Discover SharpeMetrix's systematic, data-driven investment methodology combining alternative data signals with enhanced quantitative factors to identify unique sources of alpha."
        keywords="investment methodology, alternative data, quantitative factors, alpha generation, systematic approach, data-driven investing"
        url="/methodology"
      />
      <Header />
      <div className="min-h-screen bg-bg-100">
        <main className="relative isolate">
          {/* Background Gradient */}
          <div
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#305CFE] to-[#02D3FF] opacity-20" // Using primary gradient colors
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
              }}
            />
          </div>

          {/* Header Section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-3xl pt-24 text-center sm:pt-40 pb-16 sm:pb-24">
              <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
                Our Investment Methodology
              </h1>
              <p className="mt-6 text-xl leading-8 text-text-200">
                Discover the systematic, data-driven approach SharpeMetrix employs to identify unique sources of alpha and construct differentiated investment portfolios.
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24 sm:pb-32">

            {/* Overview Section */}
            <section className="mb-16 lg:mb-24">
               <div className="mx-auto max-w-3xl text-center">
                 <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                    <DocumentChartBarIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                    Data & Discipline
                 </h2>
                 <p className="mt-6 text-lg leading-8 text-text-200">
                    We believe that a persistent edge in modern markets requires moving beyond traditional factors and embracing the power of alternative data, processed through a rigorous, quantitative lens. Our methodology systematically integrates unique, hard-to-source information with enhanced definitions of proven quantitative signals to build portfolios aimed at delivering superior risk-adjusted returns.
                 </p>
               </div>
            </section>

            {/* Alternative Data Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                Leveraging Alternative Data Signals
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
                {altDataFactors.map((factor) => (
                  <div key={factor.name} className="flex flex-col items-start">
                    <div className="flex items-center gap-x-3 mb-4">
                       <factor.icon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                       <h3 className="text-xl font-semibold leading-7 text-text-100">{factor.name}</h3>
                    </div>
                    <p className="text-base leading-7 text-text-200 mb-3">{factor.description}</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text-300 pl-2">
                        {factor.details.map((detail, index) => <li key={index}>{detail}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Quantitative Factors Section */}
            <section className="mb-16 lg:mb-24">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                Enhanced Quantitative Factors
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-y-16">
                {quantFactors.map((factor) => (
                   <div key={factor.name} className="flex flex-col items-start bg-bg-200/60 rounded-lg p-6 ring-1 ring-white/10 shadow-md">
                     <div className="flex items-center gap-x-3 mb-4">
                       <factor.icon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                       <h3 className="text-xl font-semibold leading-7 text-text-100">{factor.name}</h3>
                     </div>
                     <p className="text-base leading-7 text-text-200 mb-3 flex-auto">{factor.description}</p>
                     <ul className="list-disc list-inside space-y-1 text-sm text-text-300 pl-2">
                         {factor.details.map((detail, index) => <li key={index}>{detail}</li>)}
                     </ul>
                   </div>
                ))}
              </div>
            </section>

            {/* Portfolio Construction Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 ring-1 ring-white/10 shadow-lg">
               <div className="mx-auto max-w-3xl text-center">
                 <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                    <WrenchScrewdriverIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                    Portfolio Construction & Risk Management
                 </h2>
                 <p className="mt-6 text-lg leading-8 text-text-200">
                    Individual signals are just the beginning. Our process involves sophisticated portfolio construction techniques to translate these insights into actionable investment strategies:
                 </p>
                 <ul className="mt-6 list-disc list-inside text-left space-y-2 text-text-200 max-w-xl mx-auto">
                    <li>
                        <strong className="text-text-100">Factor Integration:</strong> Signals are carefully weighted and combined based on historical efficacy, correlation analysis, and forward-looking economic regimes.
                    </li>
                    <li>
                        <strong className="text-text-100">Optimization Engine:</strong> We utilize robust portfolio optimization techniques to balance expected alpha against predicted risk, considering factor exposures, sector constraints, and tracking error targets.
                    </li>
                    <li>
                        <strong className="text-text-100">Turnover Control:</strong> Transaction costs are explicitly considered. Our process aims for prudent turnover levels, balancing the need to capture new signals with the cost of trading.
                    </li>
                    <li>
                        <strong className="text-text-100">Systematic Rebalancing:</strong> Portfolios are rebalanced on a systematic schedule (typically monthly or quarterly) to ensure alignment with the latest data signals and risk targets.
                    </li>
                 </ul>
               </div>
            </section>

            {/* Disclaimer Section */}
             <div className="mt-16 lg:mt-24 border-t border-bg-300 pt-8">
               <p className="text-center text-sm leading-6 text-text-300">
                 Disclaimer: The information provided on this page is for informational purposes only and does not constitute investment advice, a recommendation, or an offer or solicitation to purchase or sell any securities. Past performance is not indicative of future results. All investments involve risk, including the potential loss of principal. SharpeMetrix does not guarantee any specific outcome or profit. Consult with a qualified financial advisor before making any investment decisions.
               </p>
             </div>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}
